import { FC, useState } from "react";

import { AddCourseCodeModal } from "./AddCourseModal";
import { CourseCodeListItem } from "./CourseListItem";
import { CourseDetailTermView } from "./SectionDetailTermView";

import {
  COURSE_CODE_GROUPING_ID_QUERY_PARAM,
  COURSE_CODE_ID_QUERY_PARAM,
} from "components/constants";
import { EditableField } from "components/hover-widgets/EditableField";
import { AddCourseCodeOptionMenu } from "components/menus/AddCourseOptionMenu";
import { CourseCodeGroupingOptionsMenu } from "components/menus/CourseGroupingOptionsMenu";
import { t } from "i18n/i18n";
import { Banner } from "mds/components/Banner";
import { Button } from "mds/components/Button";
import { IconText } from "mds/components/IconText";
import { showConfirmationModal } from "mds/components/Modal";
import { AddPlusIcon, FolderIcon } from "mds/icons";
import { useAppDispatch, useAppSelector } from "store/index";
import { selectCourseCodes } from "store/selectors/base";
import {
  selectCourseCodeById,
  selectCourseCodeGroupingById,
  selectCourseCodesByGroupingId,
} from "store/selectors/section";
import { localStateActions } from "store/slices/local";
import { useListSelector } from "store/store-hooks";
import { toastSuccessMessage } from "utils/alerts";
import { useQueryParamsManager } from "utils/routing";
import { onTitleChanged } from "utils/store-utils";

type CourseGroupingViewProps = {
  eventPrefix?: string;
  canEdit: boolean;
};

export const CourseCodeGroupingView: FC<CourseGroupingViewProps> = ({ eventPrefix, canEdit }) => {
  const dispatch = useAppDispatch();
  const [isEditing, setIsEditing] = useState(false);
  const { getQueryParam } = useQueryParamsManager();
  const groupingId = getQueryParam(COURSE_CODE_GROUPING_ID_QUERY_PARAM);
  const courseCodeId = getQueryParam(COURSE_CODE_ID_QUERY_PARAM);

  const handleEnableEditMode = () => {
    dispatch(localStateActions.setIsEditMode(true));
  };

  const grouping = useAppSelector((state) => selectCourseCodeGroupingById(state, groupingId));
  const courseCodesByGrouping = useListSelector((state) =>
    selectCourseCodesByGroupingId(state, groupingId),
  );
  const courseCode = useAppSelector((state) => selectCourseCodeById(state, courseCodeId));
  const allCourseCodes = useAppSelector(selectCourseCodes);

  // If no grouping_id, show the detail view for the single course code
  if (courseCode && !grouping) {
    return (
      <CourseDetailTermView canEdit={canEdit} courseCode={courseCode} eventPrefix={eventPrefix} />
    );
  }

  if (!grouping) {
    return null;
  }

  // If grouping_id exists, show the grouping with list of course codes
  const handleAddCourseCodeFromBanner = () => {
    showConfirmationModal({
      title: t("home.course_codes.add_course_code_title"),
      hideCancelConfirmButtons: true,
      // eslint-disable-next-line react/no-unstable-nested-components
      children: (onClose) => (
        <AddCourseCodeModal
          defaultGroupingId={grouping.id}
          eventName={eventPrefix ? `${eventPrefix}Add Course Code` : undefined}
          localCourseCodes={allCourseCodes.map((code) => ({ order: code.order }))}
          onClose={onClose}
        />
      ),
    });
  };

  return (
    <div className="course-detail-page h-full px-7 py-3">
      <h2 className="h2 my-0 mb-4 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <FolderIcon className="mb-[3px] text-blue" size="l" />

          <EditableField
            isEditing={isEditing}
            label={t("glossary.course_code_grouping")}
            textSize="h2"
            value={grouping.title}
            onSetIsEditing={setIsEditing}
            onValueChanged={(newTitle) => {
              onTitleChanged("course_code_groupings", grouping.id, newTitle);
              toastSuccessMessage(t("home.toasts.grouping_renamed"));
            }}
          />
        </div>

        {canEdit && (
          <div className="flex items-center gap-2">
            <CourseCodeGroupingOptionsMenu
              courseCodeGrouping={grouping}
              eventPrefix={eventPrefix}
              onRename={() => setIsEditing(true)}
            />

            <AddCourseCodeOptionMenu
              eventPrefix={eventPrefix}
              groupingId={grouping.id}
              localCourseCodes={allCourseCodes.map((code) => ({ order: code.order }))}
            />
          </div>
        )}
      </h2>

      {courseCodesByGrouping.length === 0 ? (
        <Banner
          buttonChildren={
            canEdit ? (
              <Button
                eventName={eventPrefix ? `${eventPrefix}Add Course Code` : undefined}
                kind="blue"
                onClick={handleAddCourseCodeFromBanner}
              >
                <IconText iconStart={<AddPlusIcon />} text={t("common.add.course_code")} />
              </Button>
            ) : (
              <Button
                eventName={eventPrefix ? `${eventPrefix}Enable Edit Mode` : undefined}
                kind="blue"
                onClick={handleEnableEditMode}
              >
                {t("warning.enable_edit_mode")}
              </Button>
            )
          }
          kind="info"
        >
          {canEdit
            ? t("home.course_codes.empty_grouping_banner_text")
            : t("home.course_codes.empty_grouping_banner_text_no_edit")}
        </Banner>
      ) : (
        <div role="list">
          {courseCodesByGrouping.map((code) => (
            <CourseCodeListItem
              canEdit={canEdit}
              courseCode={code}
              eventPrefix={eventPrefix}
              key={code.id}
            />
          ))}
        </div>
      )}
    </div>
  );
};
